/**
 * Free shipping threshold display component
 * Shows dynamic messaging about free shipping progress based on cart total and product price
 */

import { render, createElement as h } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { Cart } from '../helpers/cart.types'
import { Cart as CartHelper } from '../helpers/cart'

interface FreeShippingThreshold {
  min_threshold: number
  message: any
  threshold_message?: any
  available_countries?: { country_code: string }[]
}

const extractTextContent = (node: any): string => {
  if (!node) return ''
  if (typeof node === 'string') return node
  if (Array.isArray(node)) return node.map(extractTextContent).join('')
  if (typeof node === 'object') {
    if (typeof node.value === 'string') return node.value
    if (Array.isArray(node.children)) return node.children.map(extractTextContent).join('')
  }
  return ''
}

const getThresholdMessage = (t: FreeShippingThreshold) => extractTextContent(t.threshold_message)

interface FreeShippingUSPComponentProps {
  productId: string
  productPriceMin: number
  freeShippingThresholds: FreeShippingThreshold[]
  textClass?: string
  element?: HTMLElement
}

function FreeShippingUSPComponent({
  productId,
  productPriceMin,
  freeShippingThresholds = [],
  textClass,
  element
}: FreeShippingUSPComponentProps) {
  const lastMessageRef = useRef('')

  const updateMessage = (cart: Cart) => {
    if (!productId || !cart) return

    const currentCountry = window.globals?.localization?.country
    const filtered = currentCountry
      ? freeShippingThresholds.filter(
        (t) =>
          !t.available_countries?.length ||
            t.available_countries.some((c) => c.country_code === currentCountry)
      )
      : freeShippingThresholds

    if (!filtered.length) return

    const cartTotal = cart.total_price || 0
    const totalWithProduct = cartTotal + (productPriceMin || 0)
    const sorted = [...filtered].sort((a, b) => a.min_threshold - b.min_threshold)
    const reversed = [...sorted].reverse()

    const current = reversed.find((t) => cartTotal >= (t.min_threshold || 0))
    const next = reversed.find((t) => totalWithProduct >= (t.min_threshold || 0))

    let message = ''

    if (!current) {
      const lowest = sorted[0]
      const thresholdMsg = getThresholdMessage(lowest)
      message = thresholdMsg || extractTextContent(lowest.message)
    } else if (next && next.min_threshold !== current.min_threshold) {
      const thresholdMsg = getThresholdMessage(current)
      message = thresholdMsg || extractTextContent(current.message)
    } else {
      message = extractTextContent(current.message)
    }

    if (element && message && message !== lastMessageRef.current) {
      element.innerHTML = `<div class="${textClass || ''}">${message}</div>`
      lastMessageRef.current = message
    }
  }

  useEffect(() => {
    const handleCartUpdate = () => {
      if (window.Cart) {
        window.Cart.getCart().then((freshCart) => {
          if (freshCart) {
            updateMessage(freshCart as Cart)
          }
        })
      }
    }

    handleCartUpdate()

    const events = [
      CartHelper.EVENT_RENDER_MINI_CART,
      CartHelper.EVENT_RENDER_CART_PAGE,
      CartHelper.EVENT_UPDATE_CART_COUNT
    ]

    events.forEach((e) => document.addEventListener(e, handleCartUpdate))
    return () => events.forEach((e) => document.removeEventListener(e, handleCartUpdate))
  }, [productId, productPriceMin, freeShippingThresholds])

  return null
}

export class FreeShippingUSP extends HTMLElement {
  private productId = ''
  private productPriceMin = 0
  private thresholds: FreeShippingThreshold[] = []
  private textClass = ''

  connectedCallback() {
    this.parseAttributes()
    this.loadThresholds()
    this.renderComponent()
  }

  private parseAttributes() {
    this.productId = this.getAttribute('data-product-id') || ''
    this.productPriceMin = parseInt(this.getAttribute('data-product-price-min') || '0', 10)
    this.textClass = this.getAttribute('data-text-class') || ''
  }

  private loadThresholds() {
    try {
      const script = document.querySelector('[data-free-shipping-usp-data]')
      if (script) {
        this.thresholds = JSON.parse(script.textContent || '[]')
      } else if (window.globals?.metaobjects?.free_shipping_usp) {
        this.thresholds = window.globals.metaobjects.free_shipping_usp
      } else {
        console.warn('Free shipping thresholds not found')
        return
      }

      const currentCountry = window.globals?.localization?.country
      if (currentCountry) {
        this.thresholds = this.thresholds.filter(
          (t) =>
            !t.available_countries?.length ||
            t.available_countries.some((c) => c.country_code === currentCountry)
        )
      }
    } catch (err) {
      console.error('Error loading free shipping thresholds:', err)
    }
  }

  private renderComponent() {
    render(
      h(FreeShippingUSPComponent, {
        productId: this.productId,
        productPriceMin: this.productPriceMin,
        freeShippingThresholds: this.thresholds,
        textClass: this.textClass,
        element: this
      }),
      this
    )
  }
}
